import React from "react";
import { Loader2, Star, Plus, Edit3, Trash2, Eye, EyeOff, Save, X, Flame } from "lucide-react";
import api from "../../lib/api";

const EMPTY = {
  prenom: "",
  nom_initial: "",
  texte: "",
  note: 5,
  service: "",
  publie: true,
  featured: true,
  source: "",
  created_at: "",
};

function toInputDate(iso) {
  if (!iso) return "";
  return iso.slice(0, 10);
}

function toIsoDate(d) {
  if (!d) return new Date().toISOString();
  // 2026-07-15 → 2026-07-15T12:00:00+00:00
  return `${d}T12:00:00+00:00`;
}

const GoogleReviewsSync = ({ onSynced }) => {
  const [status, setStatus] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState("");
  const load = React.useCallback(() => api.get("/admin/google-reviews/status").then((r) => setStatus(r.data)), []);
  React.useEffect(() => { load(); }, [load]);
  const sync = async () => {
    setBusy(true); setMsg("");
    try { const r = await api.post("/admin/google-reviews/sync"); setMsg(`${r.data.imported} nouveau(x), ${r.data.updated} mis à jour.`); onSynced(); load(); }
    catch (e) { setMsg(e.response?.data?.detail || "Import impossible."); }
    finally { setBusy(false); }
  };
  if (!status) return null;
  return (
    <div className="mb-8 p-5 rounded border border-white/10 bg-white/[0.03] flex flex-wrap items-center gap-4" data-testid="google-reviews-sync">
      <div className="flex-1 min-w-[240px]">
        <p className="font-serif text-lg">Avis Google automatiques</p>
        <p className="text-sm text-text-cream/50 font-light" data-testid="google-reviews-status">
          {status.enabled
            ? `Import quotidien à 6 h, publication automatique · ${status.google_count} avis importés${status.last_run ? ` · dernier import ${new Date(status.last_run).toLocaleString("fr-CH")}` : ""}${status.last_error ? ` · erreur : ${status.last_error}` : ""}`
            : "Désactivé — en attente de la clé Google Places API et du Place ID."}
        </p>
      </div>
      <button onClick={sync} disabled={!status.enabled || busy} className="btn-primary inline-flex items-center gap-2 disabled:opacity-40" data-testid="google-reviews-sync-btn">
        {busy ? <Loader2 size={14} className="animate-spin"/> : <Star size={14}/>} Importer maintenant
      </button>
      {msg && <p className="w-full text-sm text-text-cream/70" data-testid="google-reviews-sync-msg">{msg}</p>}
    </div>
  );
};

export default function AdminTemoignages() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editing, setEditing] = React.useState(null); // null | 'new' | id
  const [form, setForm] = React.useState(EMPTY);
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(() => {
    setLoading(true);
    api.get("/admin/testimonials")
      .then(r => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const startNew = () => {
    setForm({ ...EMPTY, created_at: toInputDate(new Date().toISOString()) });
    setEditing("new");
  };
  const startEdit = (t) => {
    setForm({
      prenom: t.prenom || "",
      nom_initial: t.nom_initial || "",
      texte: t.texte || "",
      note: t.note || 5,
      service: t.service || "",
      publie: t.publie !== false,
      featured: t.featured !== false,
      source: t.source || "",
      created_at: toInputDate(t.created_at),
    });
    setEditing(t.id);
  };
  const cancel = () => {
    setEditing(null);
    setForm(EMPTY);
  };

  const save = async () => {
    if (!form.prenom.trim() || !form.texte.trim()) {
      alert("Prénom et texte requis");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        prenom: form.prenom.trim(),
        nom_initial: form.nom_initial.trim(),
        texte: form.texte.trim(),
        service: form.service.trim(),
        source: form.source.trim(),
        created_at: form.created_at ? toIsoDate(form.created_at) : null,
      };
      if (editing === "new") {
        await api.post("/admin/testimonials", payload);
      } else {
        await api.patch(`/admin/testimonials/${editing}`, payload);
      }
      cancel();
      load();
    } catch (e) {
      alert("Erreur enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const togglePublie = async (t) => {
    try {
      await api.patch(`/admin/testimonials/${t.id}`, { publie: !t.publie });
      load();
    } catch { alert("Erreur"); }
  };
  const toggleFeatured = async (t) => {
    try {
      await api.patch(`/admin/testimonials/${t.id}`, { featured: !t.featured });
      load();
    } catch { alert("Erreur"); }
  };
  const remove = async (t) => {
    if (!confirm(`Supprimer le témoignage de ${t.prenom} ?`)) return;
    try {
      await api.delete(`/admin/testimonials/${t.id}`);
      load();
    } catch { alert("Erreur"); }
  };

  const stats = React.useMemo(() => ({
    total: items.length,
    published: items.filter(t => t.publie).length,
    featured: items.filter(t => t.featured && t.publie).length,
  }), [items]);

  return (
    <div className="text-text-cream" data-testid="admin-temoignages">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow mb-2">Admin</div>
          <h1 className="font-serif text-4xl">Témoignages clients</h1>
          <p className="text-text-cream/50 mt-2 text-sm font-light max-w-2xl">
            {stats.total} au total · {stats.published} publiés · {stats.featured} en vedette (home)
          </p>
        </div>
        <button
          onClick={startNew}
          className="btn-primary inline-flex items-center gap-2"
          data-testid="new-testimonial-btn"
        >
          <Plus size={14} /> Nouveau témoignage
        </button>
      </div>

      <GoogleReviewsSync onSynced={load}/>

      {/* Edit/create form */}
      {editing && (
        <div className="mb-8 bg-white/[0.04] border border-accent-deep/30 rounded p-6" data-testid="testimonial-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif text-xl">
              {editing === "new" ? "Nouveau témoignage" : "Modifier le témoignage"}
            </h3>
            <button onClick={cancel} className="text-text-cream/40 hover:text-rose-300" data-testid="form-cancel">
              <X size={18} />
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-text-cream/60 text-xs uppercase tracking-wider mb-1 block">Prénom *</label>
              <input
                type="text"
                value={form.prenom}
                onChange={(e) => setForm({ ...form, prenom: e.target.value })}
                className="w-full bg-white/[0.03] border border-white/10 rounded px-3 py-2 text-sm text-text-cream focus:border-accent-deep outline-none"
                data-testid="form-prenom"
              />
            </div>
            <div>
              <label className="text-text-cream/60 text-xs uppercase tracking-wider mb-1 block">Initiale nom</label>
              <input
                type="text"
                value={form.nom_initial}
                onChange={(e) => setForm({ ...form, nom_initial: e.target.value })}
                placeholder="M."
                className="w-full bg-white/[0.03] border border-white/10 rounded px-3 py-2 text-sm text-text-cream focus:border-accent-deep outline-none"
                data-testid="form-nom-initial"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="text-text-cream/60 text-xs uppercase tracking-wider mb-1 block">Texte *</label>
            <textarea
              value={form.texte}
              onChange={(e) => setForm({ ...form, texte: e.target.value })}
              rows={5}
              className="w-full bg-white/[0.03] border border-white/10 rounded px-3 py-2 text-sm text-text-cream focus:border-accent-deep outline-none resize-y"
              data-testid="form-texte"
            />
          </div>

          <div className="grid md:grid-cols-4 gap-4 mt-4">
            <div>
              <label className="text-text-cream/60 text-xs uppercase tracking-wider mb-1 block">Note</label>
              <select
                value={form.note}
                onChange={(e) => setForm({ ...form, note: Number(e.target.value) })}
                className="w-full bg-white/[0.03] border border-white/10 rounded px-3 py-2 text-sm text-text-cream focus:border-accent-deep outline-none"
                data-testid="form-note"
              >
                {[5, 4, 3, 2, 1].map(n => <option key={n} value={n}>{n} ★</option>)}
              </select>
            </div>
            <div>
              <label className="text-text-cream/60 text-xs uppercase tracking-wider mb-1 block">Date</label>
              <input
                type="date"
                value={form.created_at}
                onChange={(e) => setForm({ ...form, created_at: e.target.value })}
                className="w-full bg-white/[0.03] border border-white/10 rounded px-3 py-2 text-sm text-text-cream focus:border-accent-deep outline-none"
                data-testid="form-date"
              />
            </div>
            <div>
              <label className="text-text-cream/60 text-xs uppercase tracking-wider mb-1 block">Service / contexte</label>
              <input
                type="text"
                value={form.service}
                onChange={(e) => setForm({ ...form, service: e.target.value })}
                placeholder="Portrait, Famille..."
                className="w-full bg-white/[0.03] border border-white/10 rounded px-3 py-2 text-sm text-text-cream focus:border-accent-deep outline-none"
                data-testid="form-service"
              />
            </div>
            <div>
              <label className="text-text-cream/60 text-xs uppercase tracking-wider mb-1 block">Source</label>
              <input
                type="text"
                value={form.source}
                onChange={(e) => setForm({ ...form, source: e.target.value })}
                placeholder="Heygoldie, Google..."
                className="w-full bg-white/[0.03] border border-white/10 rounded px-3 py-2 text-sm text-text-cream focus:border-accent-deep outline-none"
                data-testid="form-source"
              />
            </div>
          </div>

          <div className="flex items-center gap-5 mt-5">
            <label className="flex items-center gap-2 text-sm text-text-cream/80 cursor-pointer">
              <input
                type="checkbox"
                checked={form.publie}
                onChange={(e) => setForm({ ...form, publie: e.target.checked })}
                className="accent-accent-deep"
                data-testid="form-publie"
              />
              Publié
            </label>
            <label className="flex items-center gap-2 text-sm text-text-cream/80 cursor-pointer">
              <input
                type="checkbox"
                checked={form.featured}
                onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                className="accent-accent-deep"
                data-testid="form-featured"
              />
              En vedette (home)
            </label>
          </div>

          <div className="mt-6 flex items-center gap-3">
            <button
              onClick={save}
              disabled={saving}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="form-save"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Enregistrer
            </button>
            <button
              onClick={cancel}
              className="text-text-cream/60 text-sm hover:text-text-cream"
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {loading && <div className="text-text-cream/60 text-sm inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Chargement…</div>}

      {!loading && items.length === 0 && (
        <div className="text-text-cream/50 text-sm italic">Aucun témoignage. Ajoute le premier.</div>
      )}

      <div className="space-y-3">
        {items.map((t) => (
          <div
            key={t.id}
            className={`border rounded p-5 flex gap-4 items-start transition ${
              t.publie
                ? "bg-white/[0.03] border-white/10"
                : "bg-white/[0.015] border-white/5 opacity-60"
            }`}
            data-testid={`testimonial-row-${t.id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="font-serif text-base text-text-cream">
                  {t.prenom} {t.nom_initial || ""}
                </span>
                <span className="flex gap-0.5">
                  {Array.from({ length: t.note || 5 }).map((_, i) => (
                    <Star key={i} size={10} className="fill-accent-deep stroke-accent-deep" />
                  ))}
                </span>
                {!t.publie && (
                  <span className="text-[0.6rem] uppercase tracking-wider text-rose-300/80 border border-rose-400/30 bg-rose-500/10 px-2 py-0.5 rounded">
                    Brouillon
                  </span>
                )}
                {t.featured && t.publie && (
                  <span className="text-[0.6rem] uppercase tracking-wider text-accent-light border border-accent-deep/30 bg-accent-deep/10 px-2 py-0.5 rounded inline-flex items-center gap-1">
                    <Flame size={9} /> Home
                  </span>
                )}
              </div>
              <p className="text-text-cream/70 text-sm italic font-light leading-relaxed line-clamp-3">
                « {t.texte} »
              </p>
              <div className="flex items-center gap-3 mt-2 text-xs text-text-cream/40 font-light flex-wrap">
                {t.service && <span>{t.service}</span>}
                {t.source && <span className="text-accent-deep/70">{t.source}</span>}
                <span>{(t.created_at || "").slice(0, 10)}</span>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => toggleFeatured(t)}
                className={`inline-flex items-center justify-center p-2 rounded border transition ${
                  t.featured
                    ? "border-accent-deep/40 bg-accent-deep/10 text-accent-light"
                    : "border-white/10 text-text-cream/40 hover:text-accent-light hover:border-accent-deep/40"
                }`}
                title={t.featured ? "Retirer de la home" : "Mettre en vedette sur la home"}
                data-testid={`toggle-featured-${t.id}`}
              >
                <Flame size={12} />
              </button>
              <button
                onClick={() => togglePublie(t)}
                className={`inline-flex items-center justify-center p-2 rounded border transition ${
                  t.publie
                    ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
                    : "border-white/10 text-text-cream/40 hover:text-text-cream"
                }`}
                title={t.publie ? "Dépublier" : "Publier"}
                data-testid={`toggle-publie-${t.id}`}
              >
                {t.publie ? <Eye size={12} /> : <EyeOff size={12} />}
              </button>
              <button
                onClick={() => startEdit(t)}
                className="inline-flex items-center justify-center p-2 rounded border border-white/15 text-text-cream/70 hover:text-accent-light hover:border-accent-deep/40 transition"
                title="Modifier"
                data-testid={`edit-${t.id}`}
              >
                <Edit3 size={12} />
              </button>
              <button
                onClick={() => remove(t)}
                className="inline-flex items-center justify-center p-2 rounded border border-rose-500/30 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20 transition"
                title="Supprimer"
                data-testid={`delete-${t.id}`}
              >
                <Trash2 size={12} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
